import { useState } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { sendChatMessage } from '../lib/api';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';

export const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await sendChatMessage(name, email, message);
      setIsSubmitted(true);
      toast.success('Message sent! We\'ll get back to you soon.');
      setName('');
      setEmail('');
      setMessage('');
      setTimeout(() => {
        setIsSubmitted(false);
        setIsOpen(false);
      }, 3000);
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-20 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:-translate-y-1 ${
          isOpen ? 'bg-[#5C5C60]' : 'bg-[#1A1A1C]'
        }`}
        data-testid="chat-widget-toggle"
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" strokeWidth={1.5} />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" strokeWidth={1.5} />
        )}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div 
          className="fixed bottom-36 right-6 z-50 w-80 bg-white rounded-2xl shadow-xl border border-[#E5E5E5] overflow-hidden animate-fade-in-up"
          data-testid="chat-widget-panel"
        >
          {/* Header */}
          <div className="bg-[#1A1A1C] text-white px-6 py-4">
            <h3 className="font-semibold font-['Manrope']">Chat with us</h3>
            <p className="text-sm text-white/70">We typically reply within an hour</p>
          </div>

          {/* Form */}
          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <Input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-[#F3F2EE] border-0 rounded-xl focus:ring-2 focus:ring-[#1A1A1C]/20"
                  data-testid="chat-name-input"
                />
              </div>
              <div>
                <Input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-[#F3F2EE] border-0 rounded-xl focus:ring-2 focus:ring-[#1A1A1C]/20"
                  data-testid="chat-email-input"
                />
              </div>
              <div>
                <Textarea
                  placeholder="How can we help?"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="bg-[#F3F2EE] border-0 rounded-xl resize-none focus:ring-2 focus:ring-[#1A1A1C]/20"
                  data-testid="chat-message-input"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#1A1A1C] text-white rounded-full py-3 font-medium hover:bg-[#333333] transition-all"
                data-testid="chat-submit-btn"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Send className="w-4 h-4" strokeWidth={1.5} />
                    Send Message
                  </span>
                )}
              </Button>
            </form>
          ) : (
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-[#34C759]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-[#34C759]" strokeWidth={1.5} />
              </div>
              <h4 className="font-semibold text-[#1A1A1C] mb-2">Message Sent!</h4>
              <p className="text-sm text-[#5C5C60]">
                Thank you for reaching out. We'll get back to you soon.
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
